(ns client-portal.client.ai-agent.chat.data
  "Shared chat state/events/loaders/subs for the AI-agent conversation UI. Used by both
   the full-page agent (client.ai-agent.*) and the report-chat side panel opened from a
   report.

   The active conversation lives at `session-path`; `context-path` records how it was
   opened. Default (nil context) is the full-page agent, which talks to
   /api/ai-agent/query and reflects the session in the URL (`:ai-agent/session`). A
   `:report-chat` context talks to /api/reports/chat/:report-id (reports permission),
   sends the report's resolved data (read from `client.reports.individual.data`,
   preferring a stored `resolvedReportData` snapshot) as question context, and reflects
   the session on the report's own URL (`:reports/individual-session`).

   This ns reads the reports feature's db (report resolved data) but reports never
   requires it back — reports triggers chat purely via `:ai-agent/*` event dispatches —
   so the dependency stays one-way.

   Stopping a turn (`:ai-agent/stop-response`) aborts the request AND posts the turn's
   id to /api/ai-agent/cancel, which drops it server-side: the model call already in
   flight still runs to the end, but the steps after it are skipped and the turn is
   never saved, so a stopped answer doesn't come back with the session later."
  (:require [cljs.core.async :as async]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.products.data :as products.d]
            [client-portal.client.reports.individual.data :as reports.d]
            [cljs-time.core :as t]
            [cljs-time.format :as t.format]
            [shared.core.router :as router]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def root-path core.d/ai-agent-path)
(def input-path (conj root-path :input))
(def session-path (conj root-path :session))
(def pending-message-path (conj root-path :pending-message))
(def message-response-path (conj root-path :message-response))
(def chat-textarea-size-path (conj root-path :chat-textarea-size))
(def context-path (conj root-path :context))
(def session-reloading-path (conj root-path :session-reloading?))
(def report-product-path (conj root-path :report-product))
;; The past-chat list behind the Agent view's history panel (client.ai-agent.history),
;; keyed by agent. It outlives any one conversation, so `clear-chat-state` keeps it.
(def history-path (conj root-path :history))
;; The session `:ai-agent/open-session` is navigating to — see that event.
(def opening-session-path (conj root-path :opening-session))
;; Id of the turn being asked, generated per send and sent with it so
;; `:ai-agent/stop-response` can name it to the server. See `:ai-agent/cancel-turn`.
(def turn-id-path (conj root-path :turn-id))
(def cancel-turn-path (conj root-path :cancel-turn))

(def chat-panel-scroll-container-id "ai-agent-chat-panel-scroll")

;; Renderer for the report components an agent message carries, registered by
;; client.ai-agent.views so this ns and chat.views stay free of client.reports.
(defonce message-components-view* (atom nil))

;; The `:cancel` channel of the turn on the wire — a channel isn't db data, so it is
;; held here rather than in the app-db. See `turn-cancel-chan!`.
(defonce ^:private turn-cancel* (atom nil))

(defn- turn-cancel-chan!
  "The `:cancel` channel to send the next turn with, remembered so
   `:ai-agent/stop-response` can abort it. cljs-http aborts the XHR as soon as the
   channel yields (closing it counts) and then hands the loader a nil response, which
   its handlers already read as nothing to write back."
  []
  (let [cancel (async/chan)]
    (reset! turn-cancel* cancel)
    cancel))

(defn input-value [db] (get-in db input-path))
(defn session [db] (get-in db session-path))
(defn session-id [db] (not-empty (get-in db (conj session-path :id))))

(defn url-session-id
  "The session the URL names, if any."
  [db]
  (not-empty (:session-id (router/current-path-params db))))

(defn session-messages [db] (get-in db (conj session-path :messages) []))

(defn report-configuration
  "The configuration of the report the builder is currently showing, parsed from the
   latest report message's saved snapshot — the same JSON string a Save Report posts."
  [db]
  (some-> (session-messages db)
          (->> (filter :report) (last))
          (get-in [:saved-report :configuration])
          (util/json-string->map-basic)))

(defn report-public-id
  "The saved report a builder session is editing — off the route before the session has
   loaded, then off the session itself. Nil for a report that has never been saved."
  [db]
  (or (not-empty (:report-id (router/current-path-params db)))
      (not-empty (:report-public-id (session db)))))

(defn report-filters
  "Filter visibility for the report the builder is showing. Nil while a saved report's
   settings are still in flight, which callers read as not-yet-known and hold their
   filter controls back for; a report with no saved settings yet has only the
   configuration it is holding to go on."
  [db]
  (if-let [report-id (report-public-id db)]
    (reports.d/settings-filters db report-id)
    (reports.d/report-filters (get (report-configuration db) "filters"))))

(defn- with-builder-view
  "Add the header view a builder request renders its report over — the datepicker range
   and the crew type, on top of the concept/location already in `params` — minus whatever
   the report hides, so a filter it does not show contributes no value to the render."
  [params db]
  (-> params
      (merge (core.d/timeframe-date-params db))
      (cond-> (core.d/selected-crew-type db)
              (assoc :crew-type (core.d/selected-crew-type db)))
      (reports.d/without-hidden-filter-params (report-filters db))))

(defn pending-message [db] (get-in db pending-message-path))
(defn turn-id [db] (get-in db turn-id-path))
(defn chat-context [db] (get-in db context-path))
(defn report-chat? [db] (= (:kind (chat-context db)) :report-chat))
(defn opening-session [db] (get-in db opening-session-path))

(defn current-session-id
  "The chat the view is on: the one being navigated to while a history row is opening,
   else the one the URL names, else the one loaded. The history panel highlights against
   this, so a clicked row lights up on the click rather than a navigation later."
  [db]
  (or (opening-session db) (url-session-id db) (session-id db)))

(defn session-loading?
  "The URL names a session whose transcript isn't in hand yet — its load is in flight,
   or about to be dispatched by the view that owns the route. Without this the chat
   can't tell that apart from an empty conversation and renders the new-chat screen."
  [db]
  (boolean (and (url-session-id db)
                (not= (url-session-id db) (session-id db))
                (not (:error-code (session db))))))

(defn session-started?
  "Whether a conversation is under way: a session exists (loaded, or named by the URL
   and still loading) or its opening turn is in flight. The concept and location a
   question was asked under are baked into the answers it gets, so the header locks them
   for the life of a chat — starting a new one unlocks them."
  [db]
  (boolean (or (session-id db) (url-session-id db) (pending-message db))))

(defn report-chat-context-data
  "The report's resolved data to send as question context — the stored
   `resolvedReportData` snapshot when present (AI-summary reports), else the report's
   own resolved configuration — as a JSON string, or nil when the report isn't loaded."
  [db report-id]
  (let [config (get-in db (conj reports.d/by-id-path report-id :configuration))]
    (some-> (or (get config "resolvedReportData") config)
            (util/map->json-string))))

(defn report-product
  "The reference id of the product the report builder authors against — the
   switcher's selection while it is still one of the tenant's products, else their
   first one. Kept in local storage, so the pick survives a refresh. Sent with every
   builder turn: the server has no default, since the product picks both the table
   schemas the model authors against and the data source each query runs against."
  [db]
  (let [products (products.d/list-data db)
        selected (get-in (:local-storage db) report-product-path)]
    (or (some #{selected} (products.d/reference-ids products))
        (:reference-id (first products)))))

;; The full-page Agent view: one of the tenant's agents picked via ?agentId (the
;; data-question agent among them), with its own chat history panel.
(def general-agent-routes #{:ai-agent/new :ai-agent/session})

(defn vi-agent-route? [db]
  (contains? general-agent-routes (router/current-route-name db)))

(defn new-report-route? [db]
  (contains? #{:reports/new :reports/new-session} (router/current-route-name db)))

;; Routes rendering the full-page report builder — these always run the
;; report-builder agent, resolved from the loaded agent list (no query param).
(def report-builder-routes #{:reports/new :reports/new-session :reports/individual-edit})

(def vi-summary-agent-routes #{:vi/agent :vi/agent-session})

(defn report-builder-route? [db]
  (contains? report-builder-routes (router/current-route-name db)))

(defn vi-summary-agent-route? [db]
  (contains? vi-summary-agent-routes (router/current-route-name db)))

(def ^:private agent-route-context
  "Agent route -> the agent context it belongs to. Routes sharing a context are the
   same conversation with the same agent (e.g. the builder's new/session/edit routes,
   which a first message and a save navigate between), so moving between them must
   NOT reset the chat; moving to another context must."
  (merge (zipmap report-builder-routes (repeat :report-builder))
         (zipmap vi-summary-agent-routes (repeat :vi-summary))
         (zipmap general-agent-routes (repeat :general))))

(defn agent-context-key
  "Identity of the agent context `route-name` renders — its context, plus the agent
   picked via ?agentId on the general routes. Used as the react key for the full-page
   agent (see views/root-with-agent-context) so landing on a DIFFERENT agent remounts
   it with fresh state."
  [route-name query-params]
  (str (name (get agent-route-context route-name :general))
       "-"
       (:agent-id (util/->kebab-case-keywords query-params))))

(defn find-agent-by-kind [kind agents]
  (->> (util/when-not-loading agents)
       (filter #(= (get-in % [:configuration :kind]) kind))
       (first)))

(defn find-report-builder-agent [agents]
  (find-agent-by-kind "report-builder" agents))

(defn find-vi-summary-agent [agents]
  (find-agent-by-kind "voice-insights-summary" agents))

(defn report-builder-agent
  "The tenant's report-builder agent from the loaded agent list, or nil."
  [db]
  (find-report-builder-agent (get-in db core.d/agent-list-path)))

(defn vi-summary-agent
  "The tenant's Voice Insights summary agent from the loaded agent list, or nil."
  [db]
  (find-vi-summary-agent (get-in db core.d/agent-list-path)))

(defn agent-id
  "The active agent: the agent resolved by kind on the routes dedicated to one
   (report builder, Voice Insights summary), else the one picked via the ?agentId
   query param (full-page agent only)."
  [db]
  (cond
    (report-builder-route? db) (:public-id (report-builder-agent db))
    (vi-summary-agent-route? db) (:public-id (vi-summary-agent db))
    :else (some-> (router/current-query-params db)
                  (util/->kebab-case-keywords)
                  :agent-id)))

(defn clear-chat-state
  "Reset the conversation but keep the loaded agent list — nav options depend on it,
   and clearing it would make them flicker out while it refetches — and the past-chat
   history, which is a list of other conversations and so outlives this one. The picked
   report product survives on its own: it lives in local storage, not here."
  [db]
  (update-in db root-path select-keys [:list :history]))

;; The chat window always shows the session the URL names, so a route change that drops
;; or swaps the session id — the nav back to an agent's landing route, a browser back,
;; opening another session — takes the conversation with it. Forwarded from every
;; `:router/set-current-route` (registered by `:ai-agent/init`) so it catches the
;; navigations no chat event of ours ran for. A conversation with no id yet is the turn
;; that creates one: its id lands before the navigation reflecting it, so there is
;; nothing to reconcile.
(re/reg-event-db
  :ai-agent/sync-session-with-route re/middleware
  (fn [db]
    (if (and (session-id db) (not= (session-id db) (url-session-id db)))
      (clear-chat-state db)
      db)))

(re/reg-event-db
  :ai-agent/open-session re/middleware
  (fn [db [{:keys [id concept-id location-id]}]]
    ;; Reopen a past chat (from the history panel) on the concept/location it was held
    ;; under — passing them as nil clears those query params, which is how a session
    ;; held over All Locations reopens. The header then syncs the db to the new params
    ;; by dispatching concept/location selects, which normally reset the chat:
    ;; `opening-session-path` names the session being opened so `:ai-agent/filters-changed`
    ;; reads those selects as the session's own view rather than a user filter change.
    ;; The session load clears it.
    (if (= id (session-id db))
      db
      (do
        (re/dispatch [:router/go-with-merged-query-params
                      {:cid concept-id
                       :lid location-id}
                      ;; Each agent's sessions live on its own route (the VI summary
                      ;; agent resolves by kind, so it never carries ?agentId).
                      {:new-route [(if (vi-summary-agent-route? db)
                                     :vi/agent-session
                                     :ai-agent/session)
                                   {:session-id id}]}])
        (-> db
            (clear-chat-state)
            (assoc-in opening-session-path id))))))

(defn session-view-in-url?
  "The URL already names the concept/location the loaded session was held under, so a
   concept/location select is the header catching up to it (see
   `:ai-agent/restore-session-view`) rather than a user filter change — both dropdowns
   change the URL first and let the header sync the db to it, so a real change is
   always a URL the session's own view doesn't match."
  [db]
  (let [{:keys [concept-id location-id]} (session db)
        {:keys [cid lid]} (router/current-query-params db)]
    (boolean (and (session-id db)
                  concept-id
                  (= (str concept-id) (str cid))
                  ;; A chat held over All Locations is not tied to one, and a concept
                  ;; with a single location has its lid filled in by the header on its
                  ;; own — no more a view change than the empty param was.
                  (or (empty? (str location-id))
                      (= (str location-id) (str lid)))))))

(re/reg-event-db
  :ai-agent/restore-session-view re/middleware
  (fn [db [{:keys [concept-id location-id]}]]
    ;; Put the concept and location the chat was held under in the URL. Its answers are
    ;; grounded in that view, so the header has to show it however the chat was reached:
    ;; a link carrying no ?cid at all, or one carrying a different concept/location than
    ;; the chat's own. A nil lid drops the param, which is how a chat held over All
    ;; Locations reopens.
    (let [{:keys [cid lid]} (router/current-query-params db)
          session-cid (str concept-id)
          session-lid (some-> location-id (str) (not-empty))]
      (when (and concept-id (or (not= session-cid (str cid))
                                (not= (str session-lid) (str lid))))
        (re/dispatch [:router/go-with-merged-query-params {:cid session-cid
                                                           :lid session-lid}])))
    db))

(re/reg-event-fx
  :ai-agent/select-report-product re/middleware
  (fn [{:keys [local-storage]} [product]]
    {:local-storage (assoc-in local-storage report-product-path product)}))

;; Point the picker at the product a loaded builder session authors against (the
;; report config's high-level `product`). The pick outlives any one conversation —
;; it is in local storage — so without this, opening an existing report for editing
;; would keep whatever was picked last and author its next components against the
;; wrong product.
(re/reg-event-fx
  :ai-agent/seed-report-product re/middleware
  (fn [{:keys [local-storage]} [product]]
    (when (not-empty product)
      {:local-storage (assoc-in local-storage report-product-path product)})))

(re/reg-sub :ai-agent/url-session-id- url-session-id)
(re/reg-sub :ai-agent/current-session-id- current-session-id)
(re/reg-sub :ai-agent/session-loading?- session-loading?)
(re/reg-sub :ai-agent/session-started?- session-started?)
;; The agent the history panel lists, however the route names it — ?agentId on the
;; general routes, resolved by kind on the ones dedicated to an agent.
(re/reg-sub :ai-agent/resolved-agent-id- agent-id)
(re/reg-sub :ai-agent/report-product- report-product)
(re/reg-sub :ai-agent/input-value- input-value)
(re/reg-sub :ai-agent/session- session)
(re/reg-sub :ai-agent/session-messages- session-messages)
(re/reg-sub :ai-agent/context- chat-context)
(re/reg-sub :ai-agent/report-chat?- report-chat?)

(re/reg-sub
  :ai-agent/session-reloading?-
  (fn [db]
    (boolean (get-in db session-reloading-path))))

(re/reg-sub
  :ai-agent/message-response-
  (fn [db]
    (get-in db message-response-path)))

(re/reg-sub
  :ai-agent/chat-textarea-size-
  (fn [db]
    (get-in db chat-textarea-size-path)))

(re/reg-sub
  :ai-agent/pending-message-
  (fn [db]
    (get-in db pending-message-path)))

(re/reg-sub
  :ai-agent/session-agent-
  (fn [db]
    (get-in db (conj session-path :agent))))

;; Label of the active agent — "Report Chat" for the report-chat panel, otherwise the
;; loaded session's agent, else the one picked via ?agentId, falling back to a generic
;; name. `:ai-agent/selected-agent-` is registered by the full-page agent's data ns.
(re/reg-sub
  :ai-agent/display-name-
  :<- [:ai-agent/context-]
  :<- [:ai-agent/session-agent-]
  :<- [:ai-agent/selected-agent-]
  (fn [[context session-agent selected-agent]]
    (cond
      (= (:kind context) :report-chat) "Report Chat"
      :else (or (:label session-agent)
                (:label selected-agent)
                "Operator Portal Agent"))))

(re/reg-auth0-loader
  :ai-agent/load-query-response
  message-response-path
  (fn [db _]
    (let [concept-id (concepts.d/current-concept-id db)
          location-id (locations.d/current-selected-location-id db)
          session-id (session-id db)
          message (pending-message db)
          context (chat-context db)]
      (when (and concept-id (not-empty message))
        (if (= (:kind context) :report-chat)
          ;; Report follow-up chat: funnels through /api/reports (reports permission),
          ;; sending the report's resolved data as question context.
          {:method :post
           :cancel (turn-cancel-chan!)
           :url (str "/api/reports/chat/" (:report-id context))
           :json-params (cond-> {:concept-id concept-id
                                 :message message
                                 :turn-id (turn-id db)}
                          location-id
                          (assoc :location-id location-id)
                          session-id
                          (assoc :session-id session-id)
                          (report-chat-context-data db (:report-id context))
                          (assoc :report (report-chat-context-data db (:report-id context))))}
          {:method :post
           :cancel (turn-cancel-chan!)
           :url "/api/ai-agent/query"
           :json-params (cond-> {:concept-id concept-id
                                 :message message
                                 :turn-id (turn-id db)}
                          location-id
                          (assoc :location-id location-id)
                          session-id
                          (assoc :session-id session-id)
                          (not-empty (agent-id db))
                          (assoc :agent-id (agent-id db))
                          ;; The builder renders reports over the header view; without a
                          ;; datepicker range the server defaults to last 7 days.
                          ;; `:product` is required on every builder turn.
                          (report-builder-route? db)
                          (-> (with-builder-view db)
                              (assoc :product (report-product db))))}))))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :on-success (fn [db {:keys [body]}]
                (if-not (pending-message db)
                  ;; The conversation was reset while the turn was in flight (a filter
                  ;; change, a new chat, leaving the page) — its answer belongs to a chat
                  ;; that no longer exists, so it must not put that chat back.
                  db
                  (let [context (chat-context db)
                        new-session-id (or (:session-id body) (session-id db))]
                    ;; Reflect the (possibly newly created) session in the URL. Report chat
                    ;; keeps the side panel open on the report's own session route; the
                    ;; full-page agent uses its own session route.
                    (cond
                      (= (:kind context) :report-chat)
                      (when (not= (url-session-id db) new-session-id)
                        (re/dispatch [:router/go-with-merged-query-params
                                      {}
                                      {:new-route [:reports/individual-session {:report-id (:report-id context)
                                                                                :session-id new-session-id}]}]))

                      ;; Report edit sessions live on the report's own edit URL (the
                      ;; session is pre-created there) — never bounce to /ai-agent.
                      (= (router/current-route-name db) :reports/individual-edit)
                      nil

                      ;; New-report builder sessions live on the new-report session URL.
                      (new-report-route? db)
                      (when (not= (url-session-id db) new-session-id)
                        (re/dispatch [:router/go-with-merged-query-params
                                      {}
                                      {:new-route [:reports/new-session {:session-id new-session-id}]}]))

                      (vi-summary-agent-route? db)
                      (when (not= (url-session-id db) new-session-id)
                        (re/dispatch [:router/go-with-merged-query-params
                                      {}
                                      {:new-route [:vi/agent-session {:session-id new-session-id}]}]))

                      (or (not= (router/current-route-name db) :ai-agent/session)
                          (not= (url-session-id db) new-session-id))
                      (re/dispatch [:router/go-with-merged-query-params
                                    {}
                                    {:new-route [:ai-agent/session {:session-id new-session-id}]}]))
                    (cond-> db
                      ;; This turn created the session (the server titles it from the
                      ;; opening message) — drop the cached history so it shows up there.
                      (not= new-session-id (session-id db))
                      (assoc-in history-path nil)

                      :always
                      (-> (update-in session-path merge {:id new-session-id
                                                         :agent (:agent body)
                                                         :messages (conj (session-messages db) body)})
                          (assoc-in pending-message-path nil))))))
  :on-fail (fn [db]
             (if-not (pending-message db)
               db
               (-> db
                   (assoc-in (conj session-path :messages) (conj (session-messages db) {:content nil}))
                   (assoc-in pending-message-path nil))))
  :error-transf (fn [] nil))

(re/reg-auth0-loader
  :ai-agent/load-session
  session-path
  (fn [db _]
    (let [concept-id (concepts.d/current-concept-id db)
          location-id (locations.d/current-selected-location-id db)
          context (chat-context db)
          session-id (url-session-id db)
          ;; A link straight to a chat carries no ?cid — the session names the concept
          ;; it was held under and `:ai-agent/restore-session-view` puts that back in
          ;; the URL once it lands, so the load itself can't wait on one. The report
          ;; paths still need it: they re-render their report over the current view.
          needs-concept? (or (= (:kind context) :report-chat) (report-builder-route? db))]
      (when (and session-id (or concept-id (not needs-concept?)))
        (if (= (:kind context) :report-chat)
          {:method :get
           :url (str "/api/reports/chat/" (:report-id context) "/" session-id)
           :query-params {:concept-id concept-id}}
          {:method :get
           :url (str "/api/ai-agent/session/" session-id)
           :query-params (cond-> {}
                           (agent-id db)
                           (assoc :agent-id (agent-id db))
                           ;; So a builder session rehydrates its report over the header's
                           ;; current concept/location/date range rather than the view it
                           ;; was created with.
                           (report-builder-route? db)
                           (-> (assoc :concept-id concept-id)
                               (cond-> location-id (assoc :location-id location-id))
                               (with-builder-view db)))}))))
  ;; A load that lost its race with a navigation is dropped rather than written back
  ;; over whatever replaced it — the chat only ever holds the session the URL names.
  :transf (fn [resp db]
            (let [body (some-> resp :body (util/->kebab-case-keywords))]
              (when (= (:id body) (url-session-id db))
                body)))
  :on-success (fn [db resp & args]
                (let [body (some-> resp :body (util/->kebab-case-keywords))
                      reload? (boolean (some #{:reload} args))
                      ;; The header has long since caught up to whatever view this
                      ;; session was opened on — filter changes reset the chat again.
                      db (assoc-in db opening-session-path nil)]
                  (cond
                    (not= (:id body) (url-session-id db))
                    (assoc-in db session-reloading-path nil)

                    ;; The session the URL names has no transcript to show — a turn that
                    ;; never persisted, or a chat since deleted. Nothing to reopen, so
                    ;; leave the dead id behind and start a new chat.
                    (and (empty? (:messages body)) (not reload?))
                    (do
                      (re/dispatch [:ai-agent/start-new-chat])
                      db)

                    :else
                    (do
                      ;; The builder is exempt: it renders its report over whatever the
                      ;; header currently shows (that is what a reload IS), so pulling
                      ;; the header back to the session's own view would fight it.
                      (when-not (or reload? (report-builder-route? db))
                        (re/dispatch [:ai-agent/restore-session-view body]))
                      (re/dispatch [:ai-agent/seed-report-settings (:report-target-concept-ids body)])
                      ;; Only on a real load: a reload is this same session re-rendered, so
                      ;; it must not undo a product switched since it opened.
                      (when-not reload?
                        (re/dispatch [:ai-agent/seed-report-product (:report-product body)]))
                      ;; A reload (datepicker change re-rendering the report) shouldn't
                      ;; clear what the user is typing.
                      (if reload?
                        (assoc-in db session-reloading-path nil)
                        (-> db
                            (assoc-in input-path nil)
                            (assoc-in pending-message-path nil)))))))
  :on-fail (fn [db _ & args]
             ;; Same for a load that failed outright (a 404 on a chat that is gone, say)
             ;; — except a reload, which keeps the session already on screen rather than
             ;; throwing the user's work away over one failed refresh.
             (when-not (some #{:reload} args)
               (re/dispatch [:ai-agent/start-new-chat]))
             (-> db
                 (assoc-in opening-session-path nil)
                 (assoc-in session-reloading-path nil)
                 (assoc-in input-path nil)
                 (assoc-in pending-message-path nil)))
  ;; `chat.views/chat-error` keys off `:error-code`, and nothing else ever sets it — the
  ;; raw response this used to store has no such key, so a failed load was
  ;; indistinguishable from an empty conversation and silently rendered the new-chat
  ;; screen.
  :error-transf (fn [resp]
                  (assoc (util/->kebab-case-keywords resp)
                         :error-code (or (:status resp) :failed))))

(re/reg-event-db
  :ai-agent/scroll-to-user-message-by-id re/middleware
  (fn [db [id]]
    (let [el-id (str "user-message-" id)]
      (if (js/document.getElementById el-id)
        (util/scroll-to-dom-el-by-id el-id {})
        (util/wait 100 #(re/dispatch [:ai-agent/scroll-to-user-message-by-id id])))
      db)))

(re/reg-event-db
  :ai-agent/send-chat re/middleware
  (fn [db [{:keys [content-override]}]]
    (let [content (or content-override (input-value db))
          message-id (util/random-uuid-string)]
      (re/dispatch [:ai-agent/load-query-response])
      (util/wait 100 #(re/dispatch [:ai-agent/scroll-to-user-message-by-id message-id]))
      (-> db
          (assoc-in (conj session-path :messages)
                    (conj (session-messages db) {:id message-id
                                                 :content content
                                                 :timestamp (t.format/unparse (t.format/formatters :date-time) (t/now))
                                                 :speaker "user"}))
          ;; Names this turn for the length of the request, so stopping it can tell the
          ;; server which one to drop.
          (assoc-in turn-id-path (util/random-uuid-string))
          (assoc-in pending-message-path content)
          (assoc-in message-response-path nil)
          (assoc-in input-path nil)))))

(re/reg-auth0-loader
  :ai-agent/cancel-turn
  cancel-turn-path
  (fn [_ turn-id]
    ;; The turn id travels as an event arg rather than being read back off the db:
    ;; `:ai-agent/stop-response` has already cleared the turn by the time this runs.
    (when (not-empty turn-id)
      {:method :post
       :url "/api/ai-agent/cancel"
       :json-params {:turn-id turn-id}}))
  :transf (fn [_] nil)
  ;; Nothing to show either way: the chat has already moved on, and a cancel that
  ;; doesn't land just means the turn saves as it would have before.
  :error-transf (fn [] nil)
  :quiet-fail? true)

(defn- mark-question-stopped
  "Flag the question the stopped turn was answering — the last message, since the
   reply it was waiting on never arrived. The chat then shows it as asked and
   unanswered rather than as one the agent ignored."
  [db]
  (let [messages (session-messages db)
        idx (dec (count messages))]
    (if (-> messages (get idx) :speaker (= "user"))
      (assoc-in db (conj session-path :messages idx :stopped?) true)
      db)))

(re/reg-event-db
  :ai-agent/stop-response re/middleware
  (fn [db]
    ;; Drop the turn on the wire: abort the request, tell the server to discard the
    ;; turn (it can't interrupt the model call in flight, but it can skip the steps
    ;; after it and the save), and clear the pending message — which is what makes an
    ;; answer already on its way land on a chat that no longer expects one, as both
    ;; the loader's handlers check before writing anything back. The question stays in
    ;; the transcript.
    (some-> @turn-cancel* (async/close!))
    (reset! turn-cancel* nil)
    (when-let [id (turn-id db)]
      (re/dispatch [:ai-agent/cancel-turn id]))
    (-> db
        (mark-question-stopped)
        (assoc-in turn-id-path nil)
        (assoc-in pending-message-path nil)
        (assoc-in message-response-path nil))))

(re/reg-event-db
  :ai-agent/set-chat-textarea-size re/middleware
  (fn [db [size]]
    (assoc-in db chat-textarea-size-path size)))

(re/reg-event-db
  :ai-agent/start-new-chat re/middleware
  (fn [db]
    (let [context (chat-context db)]
      (cond
        (= (:kind context) :report-chat)
        (do
          ;; Drop the session-id from the URL but keep the report context + open panel,
          ;; so the next message starts a fresh session for the same report.
          (when (:report-id context)
            (re/dispatch [:router/go-with-merged-query-params {} {:new-route [:reports/individual {:report-id (:report-id context)}]}]))
          (-> db
              (assoc-in session-path nil)
              (assoc-in input-path nil)
              (assoc-in pending-message-path nil)
              (assoc-in message-response-path nil)))

        ;; An edit session is tied to its report — a new chat means leaving the
        ;; edit and returning to the report itself.
        (= (router/current-route-name db) :reports/individual-edit)
        (do
          (re/dispatch [:router/go-with-merged-query-params
                        {}
                        {:new-route [:reports/individual {:report-id (:report-id (router/current-path-params db))}]}])
          (clear-chat-state db))

        (new-report-route? db)
        (do
          (when (= (router/current-route-name db) :reports/new-session)
            (re/dispatch [:router/go-with-merged-query-params {} {:new-route [:reports/new]}]))
          (clear-chat-state db))

        (vi-summary-agent-route? db)
        (do
          (when (= (router/current-route-name db) :vi/agent-session)
            (re/dispatch [:router/go-with-merged-query-params {} {:new-route [:vi/agent]}]))
          (clear-chat-state db))

        :else
        (do
          (when (vi-agent-route? db)
            (re/dispatch [:router/go-with-merged-query-params {} {:new-route [:ai-agent/new]}]))
          (clear-chat-state db))))))

;; Header filter changes, forwarded here with the triggering event. On the builder
;; a concept/location/datepicker change re-renders the current report over the new
;; view (a session reload keeps the transcript and swaps in the re-rendered report);
;; elsewhere they reset the chat as before — except location clears, which only
;; matter on the builder.
(re/reg-event-db
  :ai-agent/filters-changed re/middleware
  (fn [db [[event-id]]]
    (cond
      (report-builder-route? db)
      (if (session-id db)
        (do
          (re/dispatch-once [:ai-agent/load-session :reload])
          (assoc-in db session-reloading-path true))
        db)

      ;; The header catching up to a session being opened from the history panel — these
      ;; selects ARE that session's view, not a user filter change (see
      ;; `:ai-agent/open-session`).
      (opening-session db)
      db

      ;; Same for a session opened by link, whose view the load put in the URL after
      ;; the fact (see `:ai-agent/restore-session-view`).
      (session-view-in-url? db)
      db

      (= event-id :locations/clear-selected)
      db

      :else
      (do
        (re/dispatch [:ai-agent/start-new-chat])
        db))))

(re/reg-event-db
  :ai-agent/open-report-edit re/middleware
  (fn [db [{:keys [report-id session-id]}]]
    ;; Open the report builder on a pre-created edit session (see :reports/edit-report).
    ;; Chat state is cleared so mounting the builder route loads the session fresh.
    (re/dispatch [:router/go-with-merged-query-params
                  {:agentId nil}
                  {:new-route [:reports/individual-edit {:report-id report-id
                                                         :session-id session-id}]}])
    (clear-chat-state db)))

(re/reg-event-db
  :ai-agent/scroll-to-bottom-of-messages re/middleware
  (fn [db]
    ;; resolve the chat's own scroll container from a message element so this
    ;; works both full-page (#core-scroll-content) and in the right panel
    (when-let [el (or (some-> (js/document.querySelector "[data-user-message]")
                              (util/find-scroll-container))
                      (js/document.querySelector "#core-scroll-content"))]
      (.scrollTo el #js {:top (.-scrollHeight el) :behavior "smooth"}))
    db))

(re/reg-event-db
  :ai-agent/open-report-chat re/middleware
  (fn [db [{:keys [report-id]}]]
    ;; Start a fresh report-chat conversation for `report-id` (a report public-id) and
    ;; open the side panel. The report's resolved data is read from the reports db at
    ;; send time, so no snapshot is threaded through here.
    (re/dispatch [:layout/show-panel-right [:ai-agent/report-chat-panel]])
    (-> db
        (clear-chat-state)
        (assoc-in context-path {:kind :report-chat
                                :report-id report-id}))))

(re/reg-event-db
  :ai-agent/resume-report-chat re/middleware
  (fn [db [{:keys [report-id session-id]}]]
    ;; Deep-link / refresh on the report session route: set the report-chat context,
    ;; open the panel, and load the existing session's transcript.
    (re/dispatch [:layout/show-panel-right [:ai-agent/report-chat-panel]])
    (re/dispatch [:ai-agent/load-session])
    (-> db
        (clear-chat-state)
        (assoc-in context-path {:kind :report-chat
                                :report-id report-id})
        (assoc-in (conj session-path :id) session-id))))

(re/reg-event-db
  :ai-agent/close-report-chat re/middleware
  (fn [db]
    (let [context (chat-context db)]
      (re/dispatch [:layout/hide-panel-right])
      ;; Return to the plain report URL (drop the session-id).
      (when (:report-id context)
        (re/dispatch [:router/go-with-merged-query-params {} {:new-route [:reports/individual {:report-id (:report-id context)}]}]))
      (-> db
          (assoc-in session-path nil)
          (assoc-in context-path nil)))))
